import { ListFilter } from '@app/components/ListFilter';
import { Pagination } from '@app/components/various/Pagination';
import { SearchTopBar } from '@app/components/various/SearchTopBar';
import { Status } from '@app/components/various/Status';
import { Table } from '@app/components/various/Table';
import { TableControls } from '@app/components/various/TableControls';
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { getIsOrderEditable } from '@app/logic/Orders';
import { DELETE_SELECTIONS_MUTATION_KEY } from '@app/services/hooks/orders/useDeleteSelections';
import { useOrdersList } from '@app/services/hooks/orders/useOrdersList';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { getSkipFirstForListing } from '@app/utils/getSkipFirstForListing';
import { useOrdersListTable } from '@app/utils/hooks/orders/useOrdersListTable';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { useOrderStatusList } from '@app/utils/hooks/useOrderStatusList';
import { useSyncPageAfterDelete } from '@app/utils/hooks/useSyncPageAfterDelete';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getOrderStatusColor } from '@app/utils/status';
import { useIsMutating } from '@tanstack/react-query';
import { IconComponentProps, Order } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyToNewSelectionModal } from '../CopyToNewSelectionModal';
import { DownloadLinesheetModal } from '../DownloadLinesheetModal';
import { useOrdersSearchParams } from '../useOrdersSearchParams';

import { OrdersListCard } from './OrdersListCard';
import { OrdersListCardSkeleton } from './OrdersListCardSkeleton';
import { OrdersListEmptyState } from './OrdersListEmptyState';

export type SortingKeys = 'orderNumber' | 'accountName' | 'buyerName' | 'poNumber' | 'itemsCount';

export const OrdersList = () => {
  const { t } = useTranslation(['orders']);
  const [activeOrder, setActiveOrder] = React.useState<Nullable<Order.Essentials>>(null);
  const { notEditable } = useOrderStatusList();
  const {
    showModal: showCopyToMySelectionModal,
    isOpen: isCopyToMySelectionModalOpen,
    hideModal: hideCopyToMySelectionModal,
  } = useModalToggle();
  const {
    showModal: showLinesheetDownloadModal,
    isOpen: isLinesheetDownloadModalOpen,
    hideModal: hideLinesheetDownloadModal,
  } = useModalToggle();
  const { handlePageChange, handleSearchChange, handleSortChange, handleStatusChange, page, search, sortOrder, status } =
    useOrdersSearchParams();

  const { trackEvent } = useAmplitude();

  const statuses = React.useMemo(() => {
    return notEditable.filter(stage => !isEmpty(stage.value)).map(stage => stage.value);
  }, [notEditable]);

  const selectedStatuses = React.useMemo(() => (isEmpty(status) ? statuses : [status]), [status, statuses]) as Order.OrderStatus[];

  const skipFirst = getSkipFirstForListing(page);

  const {
    data,
    isError,
    isLoading: isLoadingInitialQuery,
    isPlaceholderData,
  } = useOrdersList({
    data: {
      limit: DEFAULT_ITEMS_COUNT_PER_REQUEST,
      search,
      skipFirst,
      sortOrder,
      statuses: selectedStatuses,
    },
  });

  const isLoading = isLoadingInitialQuery || isPlaceholderData;
  const deliveryWindows = data?.deliveryWindows ?? [];
  const orders = data?.orders;
  const ordersCountTotal = data?.orderCount;

  useSyncPageAfterDelete({ ordersCountTotal, skipFirst });

  const hasOrders = isDefined(orders) && !isEmpty(orders);

  const filters = React.useMemo(() => {
    return notEditable.map(stage => ({
      icon: ({ className }: IconComponentProps) => (
        <span className={cx(className, 'flex items-center justify-center')}>{!isEmpty(stage.value) && <Status type={stage.value} />}</span>
      ),
      ...stage,
    }));
  }, [notEditable]);

  const shouldShowEmptyState = (!isLoading && !hasOrders) || isError;

  const handleCopyToNewSelection = React.useCallback(
    (order: Order.Essentials) => {
      setActiveOrder(order);
      showCopyToMySelectionModal();
    },
    [showCopyToMySelectionModal],
  );

  const handleLinesheetDownload = React.useCallback(
    (order: Order.Essentials) => {
      setActiveOrder(order);
      showLinesheetDownloadModal();
    },
    [showLinesheetDownloadModal],
  );

  const { columnsVisibility, table, pagination } = useOrdersListTable({
    deliveryWindows,
    handleCopyToNewSelection,
    handleLinesheetDownload,
    handlePageChange,
    handleSortChange,
    orders,
    page,
    sortOrder,
  });

  const isDeletingAnySelection = useIsMutating({ mutationKey: [DELETE_SELECTIONS_MUTATION_KEY] }) > 0;
  const downloadOnlySelected = isDefined(activeOrder) ? !getIsOrderEditable(activeOrder.status) : true;

  const orderId = activeOrder?.order;
  const orderNumber = activeOrder?.orderNumber;
  const buyer = activeOrder?.buyer;

  const handleSelectionTypeChange = (selectionType: Order.SelectionStatus) => {
    handleStatusChange(selectionType);
    trackEvent({ name: 'ordersTable.radioButton.status.change' });
  };

  const handleSearchBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!isEmpty(event.target.value)) {
      trackEvent({ name: 'ordersTable.input.search.filledin' });
    }
  };

  const handleControlsChange = (column: string, isChecked: boolean) => {
    if (!isChecked) {
      trackEvent({ name: 'ordersTable.multiselect.column.unchecked', properties: { column } });
    }
  };

  const handleSuccessDownloadLinesheetModal = () => {
    trackEvent({ name: 'downloadAction.button.linesheet.submit', properties: { path: 'ordersList' } });
  };

  const handleSuccessCopyToNewSelection = () => {
    trackEvent({ name: 'selectionAction.button.copySelection.click', properties: { path: 'ordersList' } });
  };

  return (
    <div className="flex flex-1 flex-col gap-2 px-4 lg:px-8">
      <SearchTopBar
        searchPlaceholder={t('orders:search_order')}
        title={t('orders:order_other')}
        totalCount={ordersCountTotal}
        search={search}
        onSearchChange={handleSearchChange}
        onSearchBlur={handleSearchBlur}
      >
        <ListFilter title={t('orders:select_order_type')} onChange={handleSelectionTypeChange} selected={status} filters={filters} />
        <TableControls table={table} columnsConfiguration={columnsVisibility} tableName="ordersTable" onChange={handleControlsChange} />
      </SearchTopBar>

      {shouldShowEmptyState && <OrdersListEmptyState isError={isError} />}

      {(isLoading || hasOrders) && (
        <>
          <Table
            dataTestId="ordersList"
            isLoading={isLoading || isDeletingAnySelection}
            table={table}
            getRowMarkerColor={row => getOrderStatusColor(row.original.status)}
            renderSkeletonCard={index => <OrdersListCardSkeleton key={index} />}
            renderCard={(row, index) => (
              <OrdersListCard
                key={index}
                row={row}
                deliveryWindows={deliveryWindows}
                onCopyToNewSelection={handleCopyToNewSelection}
                onLinesheetDownload={handleLinesheetDownload}
              />
            )}
          />
          <Pagination
            onChange={table.setPageIndex}
            current={pagination.pageIndex}
            totalItems={ordersCountTotal}
            isHidden={isLoadingInitialQuery}
          />
        </>
      )}
      <DownloadLinesheetModal
        isOpen={isLinesheetDownloadModalOpen}
        onClose={hideLinesheetDownloadModal}
        onSuccess={handleSuccessDownloadLinesheetModal}
        location="order table"
        orderId={orderId}
        orderNumber={orderNumber}
        downloadOnlySelected={downloadOnlySelected}
      />
      <CopyToNewSelectionModal
        isOpen={isCopyToMySelectionModalOpen}
        onClose={hideCopyToMySelectionModal}
        onSubmit={handleSuccessCopyToNewSelection}
        orderId={orderId}
        orderNumber={orderNumber}
        defaultBuyer={buyer}
      />
    </div>
  );
};
